import { useState, useEffect, useCallback } from "react";
import { CalendarIcon, Star, StarOff, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multiselect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { formatDate, cn } from "@/lib/utils";
import { type TransactionFilters, type SavedFilterPreset, type TransactionType, type TransactionStatus } from "@/types/transaction";
import { useCurrenciesList } from "@/hooks/useCurrenciesData";
import { CustomerSearchMultiSelect } from "@/components/shared/customer-search-multi-select";
import type { CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";

const transactionTypes: { value: TransactionType; label: string }[] = [
  { value: "invoice", label: "Invoice" },
  { value: "credit_note", label: "Credit Note" },
  { value: "receipt", label: "Receipt" },
  { value: "debit_adjustment", label: "Debit Adjustment" },
  { value: "credit_adjustment", label: "Credit Adjustment" },
];

const statuses: { value: TransactionStatus; label: string }[] = [
  { value: "outstanding", label: "Outstanding" },
  { value: "completed", label: "Completed" },
  { value: "overdue", label: "Overdue" },
];

interface FilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TransactionFilters;
  onApplyFilters: (filters: TransactionFilters) => void;
  savedPresets: SavedFilterPreset[];
  activePresetId: string | null;
  onApplyPreset: (preset: SavedFilterPreset) => void;
  onDeletePreset: (presetId: string) => void;
  onSavePreset: () => void;
  onClearAll: () => void;
}

export function FilterSheet({
  open,
  onOpenChange,
  filters,
  onApplyFilters,
  savedPresets,
  activePresetId,
  onApplyPreset,
  onDeletePreset,
  onSavePreset,
  onClearAll,
}: FilterSheetProps) {
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>({ ...filters });
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);

  // Sync draft with applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraftFilters({ ...filters });
    }
  }, [open, filters]);

  // Track selected customer objects for the multi-select (keeps labels in sync)
  const [selectedCustomers, setSelectedCustomers] = useState<CreatePromiseFormCustomer[]>([]);

  // Sync selected customers when the sheet opens with pre-existing filter IDs
  // (customers already selected from a previous session are kept as-is)
  useEffect(() => {
    if (open) {
      // Keep only the customers whose IDs are still in the draft filters
      setSelectedCustomers((prev) =>
        prev.filter((c) => draftFilters.customerIds.includes(parseInt(c.id, 10))),
      );
    }
  }, [open, draftFilters.customerIds]);

  const handleCustomerSelectionChange = useCallback(
    (customers: CreatePromiseFormCustomer[]) => {
      setSelectedCustomers(customers);
      setDraftFilters((prev) => ({
        ...prev,
        customerIds: customers.map((c) => parseInt(c.id, 10)),
      }));
    },
    [],
  );

  // Mode-aware data for dropdowns
  const { data: currencies } = useCurrenciesList();

  const currencyOptions = (currencies ?? []).map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }));

  const handleApply = () => {
    onApplyFilters(draftFilters);
    onOpenChange(false);
  };

  const handleClearDraft = () => {
    setSelectedCustomers([]);
    setDraftFilters({
      customerIds: [],
      transactionTypes: [],
      statuses: [],
      currencies: [],
      searchQuery: "",
      hasCase: undefined,
      hasPromise: undefined,
      onDisputeFilter: undefined,
    });
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Discard draft changes when closing without applying
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="px-6">
          <SheetTitle>Filter Transactions</SheetTitle>
          <SheetDescription>
            Apply filters to narrow down your transaction list
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-4 pl-4 mt-6">
          <div className="space-y-6 pb-4">
            {/* Saved Presets */}
            {savedPresets.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Saved Presets</Label>
                <div className="space-y-2">
                  {savedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => onApplyPreset(preset)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {activePresetId === preset.id ? (
                          <Star className="h-4 w-4 fill-primary text-primary" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium">{preset.name}</span>
                      </button>
                      {!preset.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeletePreset(preset.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Separator />
              </div>
            )}

            {/* Customer Filter */}
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerSearchMultiSelect
                selectedCustomers={selectedCustomers}
                onSelectionChange={handleCustomerSelectionChange}
                placeholder="Select customers..."
              />
            </div>

            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <MultiSelect
                options={transactionTypes}
                placeholder="Select transaction types..."
                emptyMessage="No transaction types found."
                value={draftFilters.transactionTypes}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, transactionTypes: value as TransactionType[] })
                }
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={statuses}
                placeholder="Select statuses..."
                emptyMessage="No statuses found."
                value={draftFilters.statuses}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, statuses: value as TransactionStatus[] })
                }
              />
            </div>

            {/* Currency Filter */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <MultiSelect
                options={currencyOptions}
                placeholder="Select currencies..."
                emptyMessage="No currencies found."
                value={draftFilters.currencies || []}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, currencies: value })
                }
              />
            </div>

            <Separator />

            {/* Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date-from" className="text-sm">From</Label>
                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-from"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !draftFilters.dateFrom && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateFrom ? formatDate(draftFilters.dateFrom) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateFrom ? new Date(draftFilters.dateFrom) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateFrom: date ? date.toISOString().split("T")[0] : undefined,
                          });
                          setDateFromOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date-to" className="text-sm">To</Label>
                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-to"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !draftFilters.dateTo && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateTo ? formatDate(draftFilters.dateTo) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateTo ? new Date(draftFilters.dateTo) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateTo: date ? date.toISOString().split("T")[0] : undefined,
                          });
                          setDateToOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Amount Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Amount Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount-min" className="text-sm">Min Amount</Label>
                  <Input
                    id="amount-min"
                    type="number"
                    placeholder="0"
                    value={draftFilters.amountMin ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMin: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount-max" className="text-sm">Max Amount</Label>
                  <Input
                    id="amount-max"
                    type="number"
                    placeholder="No limit"
                    value={draftFilters.amountMax ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMax: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Flags Filters */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Flags</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-dispute"
                    checked={draftFilters.onDisputeFilter ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        onDisputeFilter: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="filter-has-dispute"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-promise"
                    checked={draftFilters.hasPromise ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasPromise: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="filter-has-promise"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Promise to Pay
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-case"
                    checked={draftFilters.hasCase ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasCase: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="filter-has-case"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Collection Case
                  </label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Outstanding Balance Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Outstanding Balance</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="outstanding-min" className="text-sm">Min Outstanding</Label>
                  <Input
                    id="outstanding-min"
                    type="number"
                    placeholder="0"
                    value={draftFilters.outstandingBalanceMin ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMin: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="outstanding-max" className="text-sm">Max Outstanding</Label>
                  <Input
                    id="outstanding-max"
                    type="number"
                    placeholder="No limit"
                    value={draftFilters.outstandingBalanceMax ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMax: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <SheetFooter className="p-6 bg-background border-t">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClearDraft} className="flex-1">
              Clear All
            </Button>
            <Button variant="outline" onClick={onSavePreset} className="flex-1">
              Save Preset
            </Button>
            <Button onClick={handleApply} className="flex-1">
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
